
import { Box, Button, Divider, Paper, Typography, MenuItem, FormControlLabel, Select, TextField, Radio, RadioGroup } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import AnimationPage from '../../pages/AnimationPage';
import { errorMessages } from '../../utils/methods';
import CalendarIcon from '../CalendarIcon';
import { FormInputDropdown } from '../form/FormInputDropdown';
import { useForm } from 'react-hook-form';
import { FormInputText } from '../../form-components/FormInputText';
import { updateNotice, getNoticeById, deleteNoticeById } from '../../api/notice';
import FormInputCEditor from '../../form-components/FormInputCEditor';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BannerDeleteModal from '../banner/BannerDeleteModal';
import { useTheme } from '@mui/material/styles';
import CustomCheckbox from '../CustomCheckbox';
import { FormInputDate } from '../../form-components/FormInputDate';
import { timer } from '../../constants/constants';
import {getAllBranchOption, getHeadBranchId} from '../../api/point';
import FormInputAllSearch from '../../form-components/FormInputAllSearch';
import CustomRadioBox from '../CustomRadioBox';
import dayjs from 'dayjs';
import useUserAuth from '../../utils/useUserAuth';

// 발송 대상 옵션
const sendTargetOptions = [
  { value: 'ALL', label: '전체 회원' },
  { value: 'ACTIVE', label: '유효회원' },
  { value: 'EXPIRED', label: '만료회원' },
];

// 공개 여부 옵션
const visibilityOptions = [
  { value: 'PUBLIC', label: '공개' },
  { value: 'PRIVATE', label: '비공개' },
  { value: 'SCHEDULED', label: '기간공개' },
];

const NoticeDetail = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);
  const navigate = useNavigate();
  const canEdit = useUserAuth('COMMUNITY_NOTICE_EDIT');
  const canDelete = useUserAuth('COMMUNITY_NOTICE_DELETE');
  const [loader, setLoader] = useState(false);
  const [open, setOpen] = useState(false);
  const { id } = useParams();
  const [noticeDetail, setNoticeDetail] = useState({});
  const { handleSubmit, control, reset, watch, getValues, setValue } = useForm({ 
    defaultValues: {
      ...noticeDetail,
      selectedBranches: [], // 기본값을 빈 배열로 설정
      visibility: 'PUBLIC', // 기본값을 '공개'로 설정
      startDate: null, // FormInputDate에서 사용할 기본값
      endDate: null,   // FormInputDate에서 사용할 기본값
    }
  });

  // NoticeCreate에서 추가된 state들
  const [isPinned, setIsPinned] = useState(false);
  const [isPushEnabled, setIsPushEnabled] = useState(false);
  const [sendTarget, setSendTarget] = useState('');
  const [branches, setBranches] = useState([]); // 지점 목록
  const [selectedBranches, setSelectedBranches] = useState([]); // 선택된 지점들
  const [visibility, setVisibility] = useState('PUBLIC'); // 공개 여부 상태

  // 공지 기간 관련 상태
  const [startHour, setStartHour] = useState('00');
  const [startMinute, setStartMinute] = useState('00');
  const [endHour, setEndHour] = useState('00');
  const [endMinute, setEndMinute] = useState('00');

  // watch로 날짜 값을 감시
  const watchStartDate = watch('startDate');
  const watchEndDate = watch('endDate');


  // 지점 정보 가져오기
  const fetchBranches = async () => {
    try {
        let data;
        if (user.userRole === 'ROLE_SUPERADMIN') {
            data = await getAllBranchOption(user.token, user.branch?.branchId);
        }else{
            data = await getHeadBranchId(user.token, user.headBranch?.headBranchId);
        }
      setBranches(data || []);
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  // 지점 선택 처리 함수
  const handleBranchChange = (selected) => {
    if (selected && Array.isArray(selected)) {
      // '전체 선택' 옵션 제외
      const filteredSelected = selected.filter(branch => branch.id !== 'ALL');
      
      setSelectedBranches(filteredSelected);
      setValue('selectedBranches', filteredSelected.map(branch => branch.id));
    } else {
      setSelectedBranches([]);
      setValue('selectedBranches', []);
    }
  };

  // 공개 여부 변경 처리 함수
  const handleVisibilityChange = (event) => {
    const value = event.target.value;
    setVisibility(value);
    setValue('visibility', value);

    // 기간공개가 아닌 경우 공지 기간 초기화
    if (value !== 'SCHEDULED') {
      // setValue('startDate', null);
      setValue('endDate', null);
      setStartHour('00');
      setStartMinute('00');
      setEndHour('00');
      setEndMinute('00');
    }
  };

  const submit = async values => {
    const { title, content, startDate, endDate} = values;

    const formattedStartDate = startDate ? dayjs(startDate).format('YYYY-MM-DD') : null;
    const formattedEndDate = endDate ? dayjs(endDate).format('YYYY-MM-DD') : null;


    // 발송 대상 필수 검증
    if (isPushEnabled) {
        if (!sendTarget) {
            toast.error('발송 대상을 선택해주세요');
            return;
        }
    }

    // 지점 선택 검증
    if (selectedBranches.length < 1) {
      toast.error('지점을 선택해주세요');
      return;
    }

    if (!formattedStartDate) {
      toast.error('공지 시작일을 설정해주세요');
      return;
    }

    // 기간공개 선택 시 공지 기간 필수 검증
    if (visibility === 'SCHEDULED') {
      if (!formattedEndDate) {
        toast.error('공지 종료일을 설정해주세요');
        return;
      }
      if (new Date(formattedStartDate) > new Date(formattedEndDate)) {
        toast.error('공지 시작일은 종료일보다 이전이어야 합니다');
        return;
      }
    }

    let data = {};
    if (!content) {
      data = {
        title,
        content: '',
        isPinned,
        isPushEnabled,
        sendTarget,
        selectedBranches: selectedBranches.map(branch => branch.id),
        visibility,
        startDate: formattedStartDate,
        endDate: visibility === 'SCHEDULED' ? formattedEndDate : null,
        startTime: `${startHour}:${startMinute}`,
        endTime: visibility === 'SCHEDULED' ? `${endHour}:${endMinute}` : null
      };
    } else {
      data = {
        ...values,
        isPinned,
        isPushEnabled,
        sendTarget,
        selectedBranches: selectedBranches.map(branch => branch.id),
        visibility,
        startDate:formattedStartDate,
        endDate: visibility === 'SCHEDULED' ? formattedEndDate : null,
        startTime: `${startHour}:${startMinute}`,
        endTime: visibility === 'SCHEDULED' ? `${endHour}:${endMinute}` : null
      };
    }

    try {
      await updateNotice(data, id, user.token);
      toast.success('Notice updated successfully');
      navigate('/notice');
    } catch (error) {
      toast.error(errorMessages(error));
      setLoader(false);
    }
  };

  const getNotice = async () => {
    try {
      const data = await getNoticeById(id, user.token);
      reset(data);
      setNoticeDetail(data);

      // 기존 데이터로 추가된 필드들 초기화
      if (data.isPinned !== undefined) setIsPinned(data.isPinned);
      if (data.isPushEnabled !== undefined) setIsPushEnabled(data.isPushEnabled);
      if (data.sendTarget) setSendTarget(data.sendTarget);
      if (data.visibility) setVisibility(data.visibility);
      if (data.selectedBranches) {
        const branchData = data.selectedBranches.map(branchId => ({
          id: branchId,
          name: branches.find(branch => branch.branchId === branchId)?.branchName || branchId
        }));
        setSelectedBranches(branchData);
      }
      if (data.startDate) setValue('startDate', data.startDate);
      if (data.endDate) setValue('endDate', data.endDate);
      if (data.startTime) {
        const [hour, minute] = data.startTime.split(':');
        setStartHour(hour);
        setStartMinute(minute);
      }
      if (data.endTime) {
        const [hour, minute] = data.endTime.split(':');
        setEndHour(hour);
        setEndMinute(minute);
      }
    } catch (error) {
      console.error('API call failed:', error);
      toast.error(errorMessages(error));
    }
  };

  const deleteNotice = async () => {
    try {
      await deleteNoticeById(id, user.token);
      toast.success('Notice deleted successfully');
      navigate('/notice');
    } catch (error) {
      toast.error(errorMessages(error));
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    getNotice();
  }, [branches]); // branches가 로드된 후 getNotice 실행

  return (
    <AnimationPage>
      <Box
        component={Paper}
        elavation={2}
        sx={{
          background: '#fff',
          borderRadius: '10px',
          padding: '30px 40px',
          width: '1080px',
        }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', m: '20px' }}>
          <Box sx={{ display: 'flex' }}>
            <Link
              style={{
                textDecoration: 'none',
                color: '#3a3a3a',
                display: 'flex',
              }}
              to="/notice">
              <svg id="뒤로가기" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                <path id="Path_6534" data-name="Path 6534" d="M0,0H24V24H0Z" fill="none" />
                <circle
                  id="Ellipse_96"
                  data-name="Ellipse 96"
                  cx="11"
                  cy="11"
                  r="11"
                  transform="translate(1 1)"
                  fill="#3a3a3a"
                />
                <path
                  id="Path_6535"
                  data-name="Path 6535"
                  d="M6.393,8.579H14V9.865H6.393l3.352,3.448-.884.909L4,9.222l4.861-5,.884.909Z"
                  transform="translate(3 2.778)"
                  fill="#fff"
                />
              </svg>

              <Typography sx={{ fontSize: '18px', ml: '8px', fontWeight: '550' }}>공지사항 수정</Typography>
            </Link>
          </Box>
          <Link
            style={{
              fontSize: '14px',
              fontWeight: 550,
              color: theme.palette.primary.main,
              textDecoration: 'none',
            }}
            to="/notice">
            {t('List')}
          </Link>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: '20px',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography className="title" sx={{ mr: '20px', fontSize: '12px', fontWeight: 500, width: '75px' }}>
              {t('Title')}
            </Typography>

            <FormInputText
              color="info"
              sx={{ width: '375px' }}
              size="small"
              placeholder="공지사항 제목을 입력해주세요."
              id="outlined-search"
              type="search"
              name="title"
              rules={{ required: true }}
              control={control}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' , marginLeft: '20px' }}>
            <FormControlLabel
              control={
                <CustomCheckbox
                  checked={isPinned}
                  onChange={(e) => setIsPinned(e.target.checked)}
                />
              }
              label={t('Pin to top') || '상단고정'}
              sx={{ fontSize: '12px' }}
            />

            <FormControlLabel
              control={
                <CustomCheckbox
                  checked={isPushEnabled}
                  onChange={(e) => setIsPushEnabled(e.target.checked)}
                />
              }
              label={t('App push notification') || '앱 푸쉬 알람'}
              sx={{ fontSize: '12px' }}
            />
          </Box>
        </Box>

        {/* 발송 대상 선택 박스 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: '20px',
          }}>
          <Typography className="title" sx={{ mr: '20px', fontSize: '12px', fontWeight: 500, width: '75px' }}>
            발송 대상
          </Typography>

          <Select
            value={sendTarget}
            onChange={(e) => setSendTarget(e.target.value)}
            displayEmpty
            sx={{ width: '375px', height: '40px' }}
            size="small"
            color="info"
            required={isPushEnabled} // 앱 푸쉬 알람이 체크된 경우에만 필수
          >
            <MenuItem value="" disabled>
              발송 대상을 선택해주세요
            </MenuItem>
            {sendTargetOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* 대상 지점 선택 박스 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: '20px',
          }}>
          <Typography className="title" sx={{ mr: '20px', fontSize: '12px', fontWeight: 500, width: '75px' }}>
            대상 지점
          </Typography>

          <FormInputAllSearch
            name="selectedBranches"
            control={control}
            handleChange={handleBranchChange}
            placeholder="지점을 선택해 주세요"
            noOptionsText="선택할 지점이 없습니다"
            options={branches?.map(branch => ({
              id: branch.branchId,
              name: branch.branchName,
            }))}
            sx={{
              width: '375px',
              '.MuiAutocomplete-endAdornment': {
                top: 'calc(50% - 10px)',
              },
            }}
            multiple={true}
            rules={{ required: true }}
          />
        </Box>

        {/* 공개 여부 선택 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: '20px',
          }}>
          <Typography className="title" sx={{ mr: '20px', fontSize: '12px', fontWeight: 500, width: '75px' }}>
            공개 여부
          </Typography>

          <RadioGroup
            value={visibility}
            onChange={handleVisibilityChange}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '20px',
              '& .MuiFormControlLabel-root': {
                marginRight: 0,
              }
            }}
          >
            {visibilityOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={
                  <CustomRadioBox
                    checked={visibility === option.value}
                    onChange={handleVisibilityChange}
                    name="visibility"
                  />
                }
                label={
                  <Typography sx={{ fontSize: '12px', ml: '5px' }}>
                    {option.label}
                  </Typography>
                }
              />
            ))}
          </RadioGroup>
        </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: '20px',
            }}>
            <Typography className="title" sx={{ mr: '20px', fontSize: '12px', fontWeight: 500, width: '75px' }}>
              공지 기간 <span style={{ color: 'red' }}>*</span>
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FormInputDate
                name="startDate"
                control={control}
                placeholder={t('YYYY-MM-DD')}
                components={{
                  OpenPickerIcon: CalendarIcon,
                }}
                renderInput={params => (
                  <TextField
                    sx={{
                      '& .MuiSvgIcon-root': {
                        fill: '#0F7F85 !important',
                      },
                      width: '160px',
                    }}
                    size="small"
                    {...params}
                  />
                )}
                rules={{ required: visibility === 'SCHEDULED' }}
              />

              <Select
                value={startHour}
                onChange={(e) => setStartHour(e.target.value)}
                sx={{ width: '70px', height: '36px' }}
                size="small"
              >
                {timer.hours.map((hour) => (
                  <MenuItem key={hour} value={hour}>
                    {hour}
                  </MenuItem>
                ))}
              </Select>

              <Typography sx={{ fontSize: '14px' }}>시</Typography>

              <Select
                value={startMinute}
                onChange={(e) => setStartMinute(e.target.value)}
                sx={{ width: '70px', height: '36px' }}
                size="small"
              >
                {timer.minutes.map((minute) => (
                  <MenuItem key={minute} value={minute}>
                    {minute}
                  </MenuItem>
                ))}
              </Select>

              <Typography sx={{ fontSize: '14px' }}>분</Typography>

              {/* 공지 기간 설정 UI - 기간공개 선택 시에만 표시 */}
              {visibility === 'SCHEDULED' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Typography sx={{ fontSize: '14px', margin: '0 10px' }}>-</Typography>

                  <FormInputDate
                    name="endDate"
                    control={control}
                    placeholder={t('YYYY-MM-DD')}
                    minDate={watch('startDate') || null}
                    components={{
                      OpenPickerIcon: CalendarIcon,
                    }}
                    renderInput={params => (
                      <TextField
                        sx={{
                          '& .MuiSvgIcon-root': {
                            fill: '#0F7F85 !important',
                          },
                          width: '160px',
                        }}
                        size="small"
                        {...params}
                      />
                    )}
                    rules={{ required: visibility === 'SCHEDULED' }}
                  />

                  <Select
                    value={endHour}
                    onChange={(e) => setEndHour(e.target.value)}
                    sx={{ width: '70px', height: '36px' }}
                    size="small"
                  >
                    {timer.hours.map((hour) => (
                      <MenuItem key={hour} value={hour}>
                        {hour}
                      </MenuItem>
                    ))}
                  </Select>

                  <Typography sx={{ fontSize: '14px' }}>시</Typography>

                  <Select
                    value={endMinute}
                    onChange={(e) => setEndMinute(e.target.value)}
                    sx={{ width: '70px', height: '36px' }}
                    size="small"
                  >
                    {timer.minutes.map((minute) => (
                      <MenuItem key={minute} value={minute}>
                        {minute}
                      </MenuItem>
                    ))}
                  </Select>

                  <Typography sx={{ fontSize: '14px' }}>분</Typography>
                </Box>
                )}
            </Box>
          </Box>

          {/* 추가된 공지사항 발송 규칙 안내 */}
          <Typography
            sx={{
              mt: '10px',
              fontWeight: 500,
              ml: '95px', // 공지 기간 라벨과 맞춤
              color: '#9e9e9e',
              lineHeight: '14.32px',
              fontSize: '12px',
              width: '800px',
            }}>
            1. 오후 10시부터 오전 8시 사이의 공지사항은 익일 9시에 발송됩니다.
          </Typography>
          <Typography
            sx={{
              mt: '5px',
              fontWeight: 500,
              ml: '95px', // 공지 기간 라벨과 맞춤
              color: '#9e9e9e',
              lineHeight: '14.32px',
              fontSize: '12px',
              width: '800px',
            }}>
            2. 푸쉬 알람 발송은 매 시간 정각에 발송됩니다.<br/>
            &nbsp;&nbsp;ex) 22시 30분으로 설정시 23시에 발송
          </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: '20px',
          }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography className="title" sx={{ mr: '20px', fontSize: '12px', fontWeight: 500, width: '75px' }}>
              {t('Detail')}
            </Typography>

            <Box
              sx={{
                width: '1000px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                mt: 1,
              }}>
              <FormInputCEditor
                setValue={setValue}
                control={control}
                name="content"
                data={noticeDetail?.content ?? ''}
              />
            </Box>
          </Box>
        </Box>
        <Divider sx={{ mb: '20px', mt: '26px', color: '#e1e1e1' }} />

        <Box
          sx={{
            display: 'flex',
            mt: '10px',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}>
          {canDelete && (
            <Button
              onClick={() => setOpen(true)}
              sx={{
                p: '12px 37px 11px 37px',
                borderRadius: '5px',
                height: '36px',
                width: '100px',
                background: 'transparent',
                border: '1px solid #FF3A29',
                color: '#FF3A29',
                '&:hover': { background: '#ddd' },
              }}>
              {t('Delete')}
            </Button>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              columnGap: '6px',
            }}>
            <Button
              onClick={() => navigate(-1)}
              sx={{
                p: '12px 37px 11px 37px',
                borderRadius: '5px',
                height: '36px',
                width: '100px',
                background: '#98A6AD',
                color: '#fff',
                '&:hover': { background: '#ddd' },
              }}>
              {t('Cancel')}
            </Button>
            <Button
              onClick={handleSubmit(submit)}
              disabled={loader || !canEdit}
              sx={{
                p: '12px 56px 11px 55px',
                borderRadius: '5px',
                height: '36px',
                width: '137px',
                background: '#0F7F85',
                color: '#fff',
                '&:hover': { background: '#0F7F85', color: '#fff' },
                '&.Mui-disabled': { background: '#ccc', color: '#fff' },
              }}>
              {t('Save')}
            </Button>
          </Box>
        </Box>
        <BannerDeleteModal open={open} setOpen={setOpen} handleDelete={deleteNotice} />
      </Box>
    </AnimationPage>
  );
};

export default NoticeDetail;